// Responsive trait-by-trait comparison table. Replaces the previous
// desktop + mobile split with a single CSS-grid layout that stacks under
// 720px. Each trait row highlights the winning cell and renders the per-grass
// TraitBar fill.
//
// Pure presentational. The page passes grasses + the per-trait helpers from
// `lib/grass-comparison-scoring.ts`.

import {
  Sun,
  Droplets,
  Snowflake,
  Footprints,
  Scissors,
  DollarSign,
} from 'lucide-react'
import { GrassCharacteristics } from '@/lib/types/recommendation-engine'
import {
  GRASS_COMPARISON_TRAITS,
  TraitId,
  getTraitScore,
  pickWinnerIndex,
} from '@/lib/grass-comparison-scoring'
import { getGrassComparisonUi } from '@/data/grass-comparison-ui'
import { TraitBar } from './trait-bar'
import { cn } from '@/lib/utils'

const TRAIT_ICON: Record<TraitId, React.ComponentType<{ className?: string }>> = {
  drought: Sun,
  shade: Droplets,
  cold: Snowflake,
  traffic: Footprints,
  maint: Scissors,
  cost: DollarSign,
}

interface ComparisonTableProps {
  grasses: GrassCharacteristics[]
}

export function ComparisonTable({ grasses }: ComparisonTableProps) {
  if (grasses.length < 2) return null

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      {/* Header row — visible at ≥720px */}
      <div
        className="hidden border-b border-gray-200 bg-gray-50 min-[720px]:grid"
        style={{ gridTemplateColumns: `220px repeat(${grasses.length}, minmax(0, 1fr))` }}
      >
        <div className="flex items-end px-5 py-4 text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Trait
        </div>
        {grasses.map((g) => {
          const ui = getGrassComparisonUi(g.name)
          return (
            <div key={g.name} className="border-l border-gray-200 px-4 py-4">
              <div className="mb-1.5 flex items-center gap-2.5">
                <span
                  className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white"
                  style={{ backgroundColor: ui.accentColor }}
                  aria-hidden="true"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M5 21h14M8 21c0-5 1-8 3-10M12 21c0-7 2-10 4-13M16 21c0-4 1-6 2-8" />
                  </svg>
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold tracking-tight text-gray-900">{g.name}</p>
                  {ui.latinName && (
                    <p className="truncate text-[11px] italic text-gray-500">{ui.latinName}</p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide',
                    g.season === 'Warm' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700',
                  )}
                >
                  {g.season === 'Warm' ? 'Warm-season' : 'Cool-season'}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Mobile chip row — visible at <720px */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 min-[720px]:hidden">
        {grasses.map((g) => {
          const ui = getGrassComparisonUi(g.name)
          return (
            <span
              key={g.name}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 text-xs font-semibold text-gray-900"
            >
              <span
                className="h-4 w-4 rounded-full"
                style={{ backgroundColor: ui.accentColor }}
                aria-hidden="true"
              />
              {g.name}
            </span>
          )
        })}
      </div>

      {/* Trait rows */}
      {GRASS_COMPARISON_TRAITS.map((t) => {
        const TraitIcon = TRAIT_ICON[t.id]
        const winnerIdx = pickWinnerIndex(grasses, t.id)
        return (
          <div
            key={t.id}
            className="border-b border-gray-100 last:border-b-0 min-[720px]:grid"
            style={{ gridTemplateColumns: `220px repeat(${grasses.length}, minmax(0, 1fr))` }}
          >
            <div className="flex items-center gap-3 bg-gray-50 px-4 py-3.5 min-[720px]:bg-transparent">
              <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
                <TraitIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">{t.label}</p>
                <p className="text-xs text-gray-500">{t.blurb}</p>
              </div>
            </div>
            {grasses.map((g, gi) => {
              const score = getTraitScore(g, t.id)
              const isWinner = gi === winnerIdx
              const ui = getGrassComparisonUi(g.name)
              return (
                <div
                  key={g.name}
                  className={cn(
                    'flex flex-wrap items-center gap-2.5 px-4 py-3 min-[720px]:border-l min-[720px]:border-gray-100',
                    isWinner && 'bg-green-50/70',
                  )}
                >
                  <span className="block w-full text-[10px] font-bold uppercase tracking-wider text-gray-500 min-[720px]:hidden">
                    {g.name}
                  </span>
                  <TraitBar value={score} color={ui.accentColor} highlight={isWinner} />
                  <span className="text-xs font-semibold text-gray-700">{score}/5</span>
                  {isWinner && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-green-700">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12l4 4 10-10" />
                      </svg>
                      Wins
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )
      })}

      {/* Care quick facts footer */}
      <div
        className="border-t border-gray-200 bg-gray-50 min-[720px]:grid"
        style={{ gridTemplateColumns: `220px repeat(${grasses.length}, minmax(0, 1fr))` }}
      >
        <div className="flex items-center px-4 py-3.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">
          Care quick facts
        </div>
        {grasses.map((g) => (
          <div
            key={g.name}
            className="flex flex-col gap-1 border-t border-gray-200 px-4 py-3 text-xs text-gray-700 min-[720px]:border-l min-[720px]:border-t-0 min-[720px]:border-gray-200"
          >
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 min-[720px]:hidden">
              {g.name}
            </span>
            <div>
              <span className="text-gray-500">Mow: </span>
              <strong className="text-gray-900">
                {g.mowingDetails?.optimal ?? g.mowingHeight ?? '—'}
              </strong>
            </div>
            <div>
              <span className="text-gray-500">Water: </span>
              <strong className="text-gray-900">
                {g.wateringNeeds?.weeklyRequirements?.summer ?? 1}&quot;/wk
              </strong>
            </div>
            <div>
              <span className="text-gray-500">Establishes: </span>
              <strong className="text-gray-900">{g.establishmentTime ?? '—'}</strong>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
